import { useState } from "react"
import { CircularProgress, Grid, Modal, Typography, Button } from "src/lib"
import { IConfirmCommitmentStatusModalProps } from "./types"
import { updateCommitmentStatus } from "src/services/api/commitments"
import { useCommitmentsStore } from "src/store"

// Converte due_date (formato YYYY-MM-DD ou YYYY-M-D) para YYYYmmdd
const formatDueDate = (dueDate: string): string => {
    if (!dueDate) return ""
    
    // Remove espaços e divide por hífen
    const parts = dueDate.trim().split("-")
    if (parts.length !== 3) return ""
    
    const year = parts[0]
    const month = parts[1].padStart(2, "0")
    const day = parts[2].padStart(2, "0")
    
    return `${year}${month}${day}`
}

const ConfirmCommitmentStatusModal = ({ 
    commitmentId, 
    currentStatus, 
    clientName,
    accountId,
    dueDate,
    open, 
    onClose,
    onConfirm 
}: IConfirmCommitmentStatusModalProps) => {
    const [isUpdating, setIsUpdating] = useState<boolean>(false)
    const reloadCommitments = useCommitmentsStore((s: any) => s.reloadCommitments)

    const handleConfirm = async () => {
        // Validação dos parâmetros antes de fazer a requisição
        if (!commitmentId || commitmentId === "") {
            console.error("Erro: commitmentId é obrigatório")
            return
        }
        
        // Valida accountId: não pode ser null, undefined ou string vazia (mas pode ser 0)
        if (accountId === null || accountId === undefined || accountId === "") {
            console.error("Erro: accountId é obrigatório e não pode ser null, undefined ou vazio")
            return
        }

        // Valida dueDate
        if (!dueDate || dueDate === "") {
            console.error("Erro: dueDate é obrigatório")
            return
        }

        try {
            setIsUpdating(true)
            // Formata o due_date para o formato YYYYmmdd
            const formattedDate = formatDueDate(dueDate)
            if (!formattedDate) {
                throw new Error("Erro ao formatar due_date")
            }
            await updateCommitmentStatus(commitmentId, accountId, formattedDate)
            // Chama o callback de confirmação antes de fechar (se fornecido)
            if (onConfirm) {
                await onConfirm()
            }
            // Recarrega os dados após atualização bem-sucedida
            if (reloadCommitments) {
                await reloadCommitments()
            }
            onClose()
        } catch (error) {
            console.error("Erro ao atualizar status do commitment:", error)
            // Não fecha o modal em caso de erro, para que o usuário possa tentar novamente
        } finally {
            setIsUpdating(false)
        }
    }

    const statusText = currentStatus ? "desmarcar" : "marcar"
    const actionText = currentStatus ? "Desmarcar" : "Marcar"

    return (
        <Modal {...{ open, onClose }} size="sm" title="Confirmar alteração">
            <Grid container direction="column" gap={2} sx={{ mt: 1 }}>
                <Typography variant="body1">
                    {`Deseja ${statusText} a resolução do evento de liquidez para o cliente ${clientName || ''}?`}
                </Typography>
                
                <Grid container gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        color="neutral"
                        onClick={onClose}
                        disabled={isUpdating}
                    >
                        <Typography>Cancelar</Typography>
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        disabled={isUpdating}
                    >
                        {isUpdating ? (
                            <Grid container alignItems="center" gap={1}>
                                <CircularProgress size={16} color="primary" />
                                <Typography>Processando...</Typography>
                            </Grid>
                        ) : (
                            <Typography>{actionText}</Typography>
                        )}
                    </Button>
                </Grid>
            </Grid>
        </Modal>
    )
}

export { ConfirmCommitmentStatusModal }

